import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLocale } from "@/i18n/LocaleContext";

const ease = [0.76, 0, 0.24, 1];

const Line = ({ children, delay, accent }) => (
  <span className="mask-line">
    <motion.span
      className={`block ${accent || ""}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.25, ease, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLocale();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: videoY, scale }} className="absolute inset-0">
        <video
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster="https://images.unsplash.com/photo-1619472376731-3ca648a34b69?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          data-testid="hero-video"
        >
          <source src="https://videos.pexels.com/video-files/3129957/3129957-hd_1920_1080_25fps.mp4" type="video/mp4" />
        </video>
      </motion.div>

      <div className="absolute inset-0 bg-black/65" />
      <div className="absolute inset-0 bg-gradient-to-t from-nowi-bg via-transparent to-black/40" />

      <motion.div
        style={{ y: textY, opacity }}
        className="relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-end pb-24 md:pb-28"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex items-center gap-4 mb-8 font-mono text-[11px] uppercase tracking-[0.25em] text-nowi-muted"
          data-testid="hero-locale-badge"
        >
          <span className="w-8 h-px bg-nowi-tech" />
          {t.hero.badge}
        </motion.div>

        <h1 className="font-playfair font-medium text-[clamp(3rem,9vw,8.5rem)] leading-[0.92] tracking-[-0.02em] text-nowi-text">
          <Line delay={0.35}>{t.hero.line1}</Line>
          <Line delay={0.5} accent="italic text-nowi-venture">{t.hero.line2}</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9, ease }}
          className="mt-8 max-w-xl text-base md:text-lg text-nowi-muted"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.05, ease }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#dual"
            data-testid="hero-explore-btn"
            className="inline-flex items-center gap-3 bg-nowi-text text-nowi-bg px-7 py-4 text-xs font-mono uppercase tracking-[0.15em] hover:bg-nowi-tech transition-colors duration-300"
          >
            {t.hero.cta1} <ArrowDown className="w-4 h-4" strokeWidth={1.5} />
          </a>
          <a
            href="#contact"
            data-testid="hero-contact-btn"
            className="inline-flex items-center gap-3 border border-white/25 px-7 py-4 text-xs font-mono uppercase tracking-[0.15em] text-nowi-text hover:border-nowi-venture hover:text-nowi-venture transition-colors duration-300"
          >
            {t.hero.cta2}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 right-6 z-10 hidden md:flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-nowi-muted"
      >
        {t.hero.scroll}
        <span className="w-px h-10 bg-white/20 relative overflow-hidden">
          <motion.span
            className="absolute top-0 left-0 w-full h-1/2 bg-nowi-tech"
            animate={{ y: ["-100%", "200%"] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
};
